import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, PlayCircle } from 'lucide-react';
import { Box, Tooltip } from '@mui/material';
import { Typography, Button, Paper } from '@mui/material';

export enum TooltipRelativePosition {
  LEFT = 'left',
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'botttom'
}

export interface AppTourStep {
  targetId: string;
  title: string;
  content: string;
  position: TooltipRelativePosition | 'left' | 'right' | 'top' | 'bottom';
  preStepFunction?: () => void;
  postStepFunction?: () => void;
  onSkipTour?: () => void;
  onClose?: () => void;
}

const bgColor = 'rgb(5, 11, 19)';

export default function AppTour({ 
  steps, 
  isOpen, 
  onClose, 
  onStepChange, 
  preTourSetup,
  onSkipTour,
  videoLink
}: any){
  const [currentStep, setCurrentStep] = useState(0);
  const [showIntro, setShowIntro] = useState(true); // NEW STATE
  const [targetElement, setTargetElement] = useState<HTMLElement|null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const elevatedElsRef = useRef<Array<{ el: HTMLElement; zIndex: string; position: string }>>([]);
  const lastTargetRef = useRef<HTMLElement | null>(null);
  const lastTargetBoxShadowRef = useRef<string>('');
  const currentStepObj = steps[currentStep];
  const currentPosition = currentStepObj.position;
  const prevStepRef = useRef<number>(currentStep);

  // Handle navigation between steps
  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      // Call post-step function for the current step
      if (steps[currentStep].postStepFunction) {
        steps[currentStep].postStepFunction();
      }
      
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      if (onStepChange) {
        onStepChange(nextStep);
      }
      
      // Call pre-step function for the next step
      if (steps[nextStep].preStepFunction) {
        steps[nextStep].preStepFunction();
      }
    } else {
      // Call post-step function for the last step
      if (steps[currentStep].postStepFunction) {
        steps[currentStep].postStepFunction();
      }
      handleClose();
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      // Call post-step function for the current step
      if (steps[currentStep].postStepFunction) {
        steps[currentStep].postStepFunction();
      }
      
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      
      if (onStepChange) {
        onStepChange(prevStep);
      }
      
      // Call pre-step function for the previous step
      if (steps[prevStep].preStepFunction) {
        steps[prevStep].preStepFunction();
      }
    }
  };

  // Run pre-step function on initial render and when steps change
  useEffect(() => {
    if (isOpen && currentStepObj && currentStepObj.preStepFunction) {
      currentStepObj.preStepFunction();
    }
    
    // Cleanup - run post-step function when leaving the component or closing the tour
    return () => {
      if (isOpen && currentStepObj && currentStepObj.postStepFunction) {
        currentStepObj.postStepFunction();
      }
    };
  }, [isOpen, currentStep]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e:KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        goToNextStep();
      } else if (e.key === 'ArrowLeft') {
        goToPrevStep();
      } else if (e.key === 'Escape') {
        // Call post-step function when closing with Escape
        if (currentStepObj.postStepFunction) {
          currentStepObj.postStepFunction();
        }
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  // Position the tooltip relative to the target element
  useEffect(() => {
    if (!isOpen || !steps[currentStep]) return;

    const targetId = steps[currentStep].targetId;
    const element = document.getElementById(targetId);
    const targetPosition = steps[currentStep].position

    if (element) {
      setTargetElement(element);

      // Position calculation for tooltip
      const rect = element.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 0;
      
      // Default position below the element
      let top = rect.bottom;
      let left = rect.left + (rect.width / 2);

      if(targetPosition === 'left') {
        top = rect.top + (rect.height / 2);
        left = rect.left - ((350 + (16 * 2)) / 2);
      }
      
      // Check if tooltip would go out of viewport at bottom
      if (top + tooltipHeight > window.innerHeight) {
        // Place tooltip above the element instead
        top = rect.top - tooltipHeight;
      }
      setTooltipPosition({ top, left });
    }
  }, [currentStep, isOpen, steps]);
  

  // Apply styles to highlight and elevate the target element (and any stacking-context ancestors)
  useEffect(() => {
    // Restore any previously elevated elements first
    elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
      el.style.zIndex = zIndex;
      el.style.position = position;
    });
    elevatedElsRef.current = [];

    if (!targetElement || !isOpen) {
      // Also restore last target highlight if present
      if (lastTargetRef.current) {
        lastTargetRef.current.style.boxShadow = lastTargetBoxShadowRef.current;
        lastTargetRef.current = null;
        lastTargetBoxShadowRef.current = '';
      }
      return;
    }

    // Helper to elevate an element above the tour backdrop (9998) but below the tooltip (12000)
    const elevate = (el: HTMLElement) => {
      const prev = { el, zIndex: el.style.zIndex, position: el.style.position };
      const computed = window.getComputedStyle(el);
      if (computed.position === 'static') {
        el.style.position = 'relative';
      }
      // Keep below tooltip (12000) but above overlay (9998)
      el.style.zIndex = '11000';
      elevatedElsRef.current.push(prev);
    };

    // Elevate the target element
    elevate(targetElement);
    // Add a visual highlight
  const originalBoxShadow = targetElement.style.boxShadow;
  targetElement.style.boxShadow = '0 0 20px 5px rgba(255, 255, 255, 0.7)';
  lastTargetRef.current = targetElement;
  lastTargetBoxShadowRef.current = originalBoxShadow;

    // Walk up ancestors and elevate any that create a stacking context (z-index/transform/etc.)
    let parent: HTMLElement | null = targetElement.parentElement;
    while (parent && parent !== document.body) {
      const cs = window.getComputedStyle(parent);
      const createsStackingContext =
        cs.zIndex !== 'auto' ||
        cs.transform !== 'none' ||
        cs.perspective !== 'none' ||
        cs.filter !== 'none' ||
        cs.mixBlendMode !== 'normal' ||
        cs.position !== 'static' ||
        cs.willChange.includes('transform') ||
        cs.willChange.includes('position');
      if (createsStackingContext) {
        elevate(parent);
      }
      parent = parent.parentElement;
    }

    // Clean up: restore everything we changed
    return () => {
      targetElement.style.boxShadow = originalBoxShadow;
      elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
        el.style.zIndex = zIndex;
        el.style.position = position;
      });
      elevatedElsRef.current = [];
    };
  }, [targetElement, isOpen]);

  // When the tour closes, ensure any focus and elevated styles are removed
  useEffect(() => {
    if (!isOpen) {
      // If focused element is inside the last target, blur it
      const active = document.activeElement as HTMLElement | null;
      if (active && lastTargetRef.current && lastTargetRef.current.contains(active)) {
        try { active.blur(); } catch {}
      }
      // Restore any elevated elements
      elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
        el.style.zIndex = zIndex;
        el.style.position = position;
      });
      elevatedElsRef.current = [];
      // Restore last target highlight, if any
      if (lastTargetRef.current) {
        lastTargetRef.current.style.boxShadow = lastTargetBoxShadowRef.current;
        lastTargetRef.current = null;
        lastTargetBoxShadowRef.current = '';
      }
    }
  }, [isOpen]);

  useEffect(()=>{
    if(isOpen && preTourSetup){
      preTourSetup()
    }
  },[isOpen])

  const handleClose = () => {
    setCurrentStep(0);
    // Proactively blur if target or its child is focused
    const active = document.activeElement as HTMLElement | null;
    if (active && targetElement && targetElement.contains(active)) {
      try { active.blur(); } catch {}
    }
    if (onClose) onClose();
  };

  // Handle clicks outside the tooltip to close the tour
  useEffect(() => {
    if (!isOpen) return;
    
    const handleOutsideClick = (e: MouseEvent) => {
      // Skip if we're still in the intro
      if (showIntro) return;
      
      // Check if the click is on the tooltip or its children
      const isClickOnTooltip = tooltipRef.current?.contains(e.target as Node);
      // Check if click is on the target element (which should remain clickable)
      const isClickOnTarget = targetElement?.contains(e.target as Node);
      // Check if click is on the close button (which has its own handler)
      const closeButton = document.querySelector('[data-close-tour="true"]');
      const isClickOnCloseButton = closeButton?.contains(e.target as Node);
      
      // If the click is outside tooltip and target, close the tour
      if (!isClickOnTooltip && !isClickOnTarget && !isClickOnCloseButton) {
        if (currentStepObj.postStepFunction) {
          currentStepObj.postStepFunction();
        }
        handleClose();
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, showIntro, targetElement, currentStepObj]);

  // Fix position name typo in enum
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      prevStepRef.current = currentStep;
    }
  }, [currentStep]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || showIntro) return;
      
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          goToNextStep();
          break;
        case 'ArrowLeft':
          goToPrevStep();
          break;
        case 'Escape':
          if (currentStepObj?.postStepFunction) {
            currentStepObj.postStepFunction();
          }
          handleClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, showIntro, currentStepObj]);

  if (!isOpen) return null;

    // Show intro modal before the tour starts
  if (showIntro) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 13000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(10, 20, 40, 0.85)',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 3,
            minWidth: 340,
            maxWidth: '90vw',
            textAlign: 'center',
            bgcolor: 'rgb(5, 11, 19)',
            color: 'white',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Welcome
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Would you like a quick walkthrough of the main features?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowIntro(false)}
              sx={{ fontWeight: 700 }}
            >
              Start Tour
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setShowIntro(false);
                if (onSkipTour) onSkipTour();
                handleClose();
              }}
              sx={{ fontWeight: 700, color: 'white', borderColor: 'white' }}
            >
              Skip
            </Button>
          </Box>
          {videoLink && (
            <Box sx={{ 
              mt: 2,
              display: 'flex',
              justifyContent: 'center', 
              // backgroundColor: 'blue'
            }}>
              <Button
                href={videoLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                color="primary"
                startIcon={<PlayCircle size={18} />}
                sx={{ fontWeight: 700, color: 'white', textDecoration: 'underline' }}
              >
                Watch Video
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  if (!currentStepObj) return null;
  
  return (
    <>
      <Box
      sx={{
        top: 0,
        left: 0,
        position: 'fixed',
        zIndex: 9998,
        height: '100vh',
        width: '100vw',
        opacity: 0.7,
        background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
      }}
      />
      <Box
      sx={{
        position: 'fixed',
        zIndex: 9998,
        height: '100vh',
        width: '100vw',
        opacity: 0.7,
        background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
      }}
      />
      <Box 
      onClick={handleClose}
      sx={{
        position: 'fixed',
        top: 20,
        left: 20,
        zIndex: 12000,
        cursor: 'pointer',
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
        pointerEvents: 'auto'
      }}
      >
      ✕
      </Box>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 11000, pointerEvents: 'none' }}>
      <Tooltip
        open={true}
        placement={currentPosition as "left" | "right" | "top" | "bottom"}
        PopperProps={{
        anchorEl: targetElement,
        modifiers: [
          {
          name: 'offset',
          options: {
            offset: [0, 0], // Reduced offset to bring tooltip closer to element
          },
          },
        ],
        sx: { zIndex: 12000 }
        }}
        componentsProps={{
        tooltip: {
          sx: {
          margin: '10px',
          bgcolor: bgColor,
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          p: 2,
          maxWidth: 'none',
          width: 350,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 12000
          },
        },
        arrow: {
          sx: { 
          zIndex: 12000,
          color: bgColor, // Match arrow color with tooltip background
          fontSize: '1.5rem', // Increase arrow size
          '&::before': {
            width: 16,
            height: 16,
            boxShadow: '0 1px 0 rgba(255, 255, 255, 0.1)'
          }
          }
        }
        }}
        title={
        <Box ref={tooltipRef}>
          <Box sx={{ mb: 3 }}>
          <Box sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>{steps[currentStep].title}</Box>
          <Box sx={{ 
            fontSize: 15, 
            opacity: 1, 
            lineHeight: 1.7, 
            fontWeight: 400,
            letterSpacing: '0.015em'
          }}>
            {steps[currentStep].content}
          </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ fontSize: 12, opacity: 0.7 }}>
            {currentStep + 1} of {steps.length}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {currentStep > 0 && (
            <Box 
              onClick={goToPrevStep}
              sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              pointerEvents: 'auto'
              }}
            >
              <ChevronLeft size={18} />
            </Box>
            )}
            
            <Box 
            onClick={goToNextStep}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
              height: 28,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: 13,
              '&:hover': { bgcolor: 'primary.dark' },
              pointerEvents: 'auto'
            }}
            >
            {currentStep < steps.length - 1 ? 'NEXT' : 'FINISH'}
            </Box>
          </Box>
          </Box>
        </Box>
        }
        arrow
      >
        <span style={{ display: 'inline-block' }}></span>
      </Tooltip>
      </Box>
    </>
  )
}